import { Component } from 'react';
import { connect } from 'react-redux';

import Button from 'components/atoms/Button';
import DropList from 'components/atoms/DropList';
import Form from 'components/atoms/Form';
import FormGroup from 'components/atoms/FormGroup';
import Icon from 'components/atoms/Icon';
import Input from 'components/atoms/Input';
import Label from 'components/atoms/Label';
import { USER_TYPE, YES_AND_NO } from 'data/static';
import { bindActionCreators, Dispatch } from 'redux';
import { userCreate } from 'services/users';
import { setErrorHandlerAction, SetErrorHandlerAction } from 'store/actions/errorsActions';
import { PortType, RootState, UserType } from 'types';
import { ActionsType } from 'types/system';
import { renderError, setKey } from 'utils/formHandlers';
import { formatServerErrors } from 'utils/formatServerErrors';
import { hasPermission } from 'utils/permission';
import { translate } from 'utils/translate';

interface CreateUserModelProps {
    children?: string | JSX.Element | JSX.Element[];
    language: string;

    id?: string;
    data: any;
    port: PortType;
    user: UserType;
    actions: ActionsType | undefined;
    setErrorHandlerAction: SetErrorHandlerAction;
}

interface CreateUserModelState {
    errors: object;
    data: any | null;
    user: {
        first_name?: string;
        last_name?: string;
        email?: string;
        user_rules?: string[];
        type?: string;
        long_token?: string;
    };
}

class CreateUserModel extends Component<CreateUserModelProps, CreateUserModelState> {
    constructor(props: CreateUserModelProps) {
        super(props);
        this.state = {
            data: props.data,
            errors: {},
            user: {},
        };
    }

    handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();
        this.setState({ errors: {} }, () => {
            userCreate(this.props.port.id, this.state.user)
                .then(() => {
                    this.props.actions?.onConfirm();
                    this.props.actions?.onCancel();
                })
                .catch((errors) => {
                    switch (errors.response?.status) {
                        case 422:
                            this.setState({ errors: formatServerErrors(errors) });
                            break;
                        default:
                            this.props.setErrorHandlerAction(errors);
                            break;
                    }

                    return;
                });
        });
    };

    handleFormCancel = () => {
        this.props.actions?.onCancel();
    };

    handleChange = (name: string, value: unknown) => {
        const { user, errors } = this.state;
        setKey(user, name, value);
        setKey(errors, name, null);
        this.setState({ user, errors });
    };

    render() {
        return (
            <>
                <Form onSubmit={this.handleFormSubmit} className="bg-white shadow p-4 w-full rounded">
                    <div id={`Drag-${this.props.id}`} className="flex justify-between mb-4 no-select ">
                        <div className="text-lg leading-6 font-bold">{translate('Create user')} </div>
                        <div
                            onTouchEnd={this.handleFormCancel}
                            onClick={this.handleFormCancel}
                            className="cursor-pointer"
                        >
                            <Icon>
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M18 6l-12 12" />
                                <path d="M6 6l12 12" />
                            </Icon>
                        </div>
                    </div>

                    <FormGroup comments={renderError(this.state.errors?.['first_name'])} required={true}>
                        <Label>{translate('First name')}</Label>
                        <Input name="first_name" value={this.state?.user.first_name} onChange={this.handleChange} />
                    </FormGroup>
                    <FormGroup comments={renderError(this.state.errors?.['last_name'])} required={true}>
                        <Label>{translate('Last name')}</Label>
                        <Input name="last_name" value={this.state?.user.last_name} onChange={this.handleChange} />
                    </FormGroup>
                    <FormGroup comments={renderError(this.state.errors?.['email'])} required={true}>
                        <Label>{translate('Email')}</Label>
                        <Input name="email" value={this.state?.user.email} onChange={this.handleChange} />
                    </FormGroup>

                    {hasPermission(this.props.user, this.props.port.id, ['isAdmin']) && (
                        <FormGroup comments={renderError(this.state.errors?.['long_token'])} required={true}>
                            <Label>{translate('Long life token')}</Label>
                            <DropList  capitalize
                                name="long_token"
                                id="type"
                                value={this.state.user?.long_token || 'false'}
                                options={YES_AND_NO}
                                onChange={this.handleChange}
                            ></DropList>
                        </FormGroup>
                    )}
                    {hasPermission(this.props.user, this.props.port.id, ['isAdmin']) && (
                        <FormGroup comments={renderError(this.state.errors?.['type'])} required={true}>
                            <Label>{translate('Cluster admin')}</Label>
                            <DropList  capitalize
                                name="type"
                                id="type"
                                value={this.state.user?.type || 'editor'}
                                options={USER_TYPE}
                                onChange={this.handleChange}
                            ></DropList>
                        </FormGroup>
                    )}

                    <div className="flex justify-end">
                        <div className="mt-2 gap-3 flex">
                            <Button
                                className="px-4 py-1 font-bold border border-gray-200"
                                onClick={this.handleFormCancel}
                            >
                                {translate('Cancel')}
                            </Button>
                            <Button type="submit" className="px-4 py-2 bg-primary-submit rounded-md text-white">
                                {translate('Save')}
                            </Button>
                        </div>
                    </div>
                </Form>
            </>
        );
    }
}

const CreateUserModelStateToProps = (state: RootState) => ({
    language: state.user.user.language,
    port: state.port.port,
    user: state.user.user,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CreateUserModelDispatchToProps = (dispatch: Dispatch) => ({
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
});

export default connect(CreateUserModelStateToProps, CreateUserModelDispatchToProps)(CreateUserModel);
